import { decodeString, humanizeCount, S3ImageBaseUrl } from '@findemdev/findem-helpers';
import { formatCompanyRevenue, formatRevenueLabel } from '../../../utils/companyRevenue';
import type { PresentationState } from '../reducers/presentationSlice';
// types
import {
  DefaultColsValue,
  EDITABLE_COLUMN_FIELDS,
  IBaseCredentialMetaData,
  ICredBuilderImgUploadPayload,
  ICredBuilderImgUploadResponse,
  ICredential,
  ICredentialMetaData,
  IGenerateDefaultColsData,
  ITableColumns,
  ITemplateSortingState
} from '../types/credential';
import { CredLogo, ISelectedCandidates, ITeam } from '../types/project';
import { ILocation } from '../../../types/profile';
import { GetPrimaryLogoUrl, IAssigmentsTableData, IAssigmentsTableDataWithRequiredIds, IGetAssignmentTableMappedColumnValueParams, SortGroupedAssignmentKeys, SortGroupedAssignments } from '../types/common';
import { HoverAction } from '../components/Credential/AssignmentsTable/HoverActionCell/HoverActionCell';
import { Column, SortingFn } from '@tanstack/react-table';
import { CSSObject } from 'styled-components';
import {
  createGridTemplateAreasTemplateStandard, generateGridTemplateColumnsTemplateStandard, getGroupedOrderedAssignmentsFromLayout, GRID_AREA_PREFIX,
  GROUPED_ASSIGNMENTS_REFS_DELIMITER, MAX_COLS_TEMAPLTE_STANDARD
} from '../components/Credential/Presentation/util/standardTemplate';
import { IFundingHistory } from '../../../types/shared';
import { GroupedAssignments, GroupedAssignmentsKey, NullableGroupedAssignments } from '../components/Credential/Presentation/types/common';
import { isNonAssignmentKeyInGroupedAssignment } from '../components/Credential/Presentation/util/util';
import dayjs from 'dayjs';


/**
 *
 * @param date: date string
 * @returns formatted date e.g Month/Day/Year
 */
export const formatDate = (date: string | null): string => {
  const formattedDate = date ? new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : '';

  return formattedDate;
}

const LAST_MODIFIED_DATE_FORMAT = 'MMM DD, YYYY';

export const formatLastModifiedDate = ({ updatedAt }: { updatedAt?: string }): string => {
  if (!updatedAt) {
    return '-';
  }

  const parsedDate = dayjs(updatedAt);
  if (!parsedDate.isValid()) {
    return '-';
  }

  return parsedDate.format(LAST_MODIFIED_DATE_FORMAT);
}

export const AsisgnmentTableColumnsKeys = [
  'label',
  'clientName',
  'functionalArea',
  'title',
  'assignmentStatus',
  'startDate',
  'clientCompanyActiveInvestors',
  'endDate',
  'assignmentIndustrySector',
  'assignmentIndustryPractice',
  'personIndustry',
  'assignmentIndustry',
  'industry',
  'competitors',
  'location',
  'positionClientLocationCompanyHq',
  'billingClientLocation',
  'positionAssignmentRegion',
  'assignmentClientCompanyRegion',
  'selectedCandidates',
  'hqLocation',
  'assignmentClientCompanyStockIndex',
  'assignmentBillingClientCompanyStockIndex',
  'assignmentBillingClientCompanyParentCompanyName',
  'assignmentClientCompanyParentCompanyName',
  'billedClientName',
  'orgType',
  'businessModel',
  'companyEmployeesSize',
  'privateEquityType',
  'revenue',
  'assignmentClientCompanyRRACandidEstRevenue',
  'assignmentBillingClientCompanyRRACandidEstRevenue',
  'assignmentClientCompanyRRACandidExpenses',
  'assignmentBillingClientCompanyRRACandidExpenses',
  'funding',
  'marketCap',
  'parentCompanyFindem',
  'serviceLineGroup',
  'assignmentType',
  'reportingLevel',
  'leadConsultant',
  'leadStillAtRRA',
  'coLead',
  'region',
  'officeName',
  'hub',
  'confidentiality',
  'serviceLineType',
  'capability',
] as const;

export type AssignmentTableColumnsKeysType = typeof AsisgnmentTableColumnsKeys[number];

export type AssignmentTableColumnsKeysTypeWithSelect = AssignmentTableColumnsKeysType | 'select';

export const DEFAULT_APPLIED_FIELDS_STANDARD_TEMPLATES: {
  id: AssignmentTableColumnsKeysType,
  columnKey: AssignmentTableColumnsKeysType,
}[] = [
    {
      id: 'title',
      columnKey: 'title',
    },
    {
      id: 'startDate',
      columnKey: 'startDate',
    },
  ];

export const NUMERIC_COLUMNS: AssignmentTableColumnsKeysTypeWithSelect[] = [
  'revenue',
  'marketCap',
  'companyEmployeesSize',
  'assignmentClientCompanyRRACandidEstRevenue',
  'assignmentBillingClientCompanyRRACandidEstRevenue',
  'assignmentClientCompanyRRACandidExpenses',
  'assignmentBillingClientCompanyRRACandidExpenses'
];
export const DATE_COLUMNS: AssignmentTableColumnsKeysTypeWithSelect[] = ['startDate', 'endDate'];
export const DEFAULT_SORTED_BY_FIELD: NonNullable<ITemplateSortingState['sorted_by_field']> = 'clientName';

export const DEFAULT_SORTING_STATE: ITemplateSortingState = {
  sorted_by_field: DEFAULT_SORTED_BY_FIELD,
  value: 'asc',
};

export const DEFAULT_TILE_HEADER_KEY: AssignmentTableColumnsKeysType = 'clientName';

export const AssignmentTableColumns: Record<AssignmentTableColumnsKeysType, DefaultColsValue> = {
  'label': {
    title: {
      value: 'Label',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: true,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'clientName': {
    title: {
      value: 'Client Name (Position)',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'functionalArea': {
    title: {
      value: 'Functional Area',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'title': {
    title: {
      value: 'Position Title',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'assignmentStatus': {
    title: {
      value: 'Assignment Status',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'startDate': {
    title: {
      value: 'Start Date',
      isModified: false,
    },
    icon: 'event',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'clientCompanyActiveInvestors': {
    title: {
      value: 'RRA Investors',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'endDate': {
    title: {
      value: 'Year of Project Completion',
      isModified: false,
    },
    icon: 'event',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'assignmentIndustrySector': {
    title: {
      value: 'RRA Industry',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'assignmentIndustryPractice': {
    title: {
      value: 'RRA Practice',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'assignmentIndustry': {
    title: {
      value: 'RRA PIC',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'personIndustry': {
    title: {
      value: 'Industry',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: false,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'industry': {
    title: {
      value: 'Findem Industry',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'competitors': {
    title: {
      value: 'Competitors',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'location': {
    title: {
      value: 'Position Location',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'billingClientLocation': {
    title: {
      value: 'Position Client Location (Company HQ)',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'positionClientLocationCompanyHq': {
    title: {
      value: 'Billing Client Location',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'positionAssignmentRegion': {
    title: {
      value: 'Region (Position)',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'assignmentClientCompanyRegion': {
    title: {
      value: 'Region (Company HQ)',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'selectedCandidates': {
    title: {
      value: 'Successful Candidate',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'hqLocation': {
    title: {
      value: 'Company HQ Location',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'assignmentClientCompanyStockIndex': {
    title: {
      value: 'Client Company Stock Index (Position)',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'assignmentBillingClientCompanyStockIndex': {
    title: {
      value: 'Client Company Stock Index (Billing)',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'assignmentClientCompanyParentCompanyName': {
    title: {
      value: 'RRA Parent Company (Position)',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'assignmentBillingClientCompanyParentCompanyName': {
    title: {
      value: 'RRA Parent Company (Billing)',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'billedClientName': {
    title: {
      value: 'Client Name (Billed)',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'orgType': {
    title: {
      value: 'Org Type',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'businessModel': {
    title: {
      value: 'Business Model',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'companyEmployeesSize': {
    title: {
      value: 'Company Size',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'privateEquityType': {
    title: {
      value: 'Private Capital Type',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'revenue': {
    title: {
      value: 'Company Revenue',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'assignmentClientCompanyRRACandidEstRevenue': {
    title: {
      value: 'Candid Revenue (SEC) (Position)',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'assignmentBillingClientCompanyRRACandidEstRevenue': {
    title: {
      value: 'Candid Revenue (SEC) (Billing)',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'assignmentClientCompanyRRACandidExpenses': {
    title: {
      value: 'Candid Expenses (SEC) (Position)',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'assignmentBillingClientCompanyRRACandidExpenses': {
    title: {
      value: 'Candid Expenses (SEC) (Billing)',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'funding': {
    title: {
      value: 'Company Funding',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'marketCap': {
    title: {
      value: 'Market Cap',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'parentCompanyFindem': {
    title: {
      value: 'Parent Company (Findem)',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'serviceLineGroup': {
    title: {
      value: 'Service Line Group',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'assignmentType': {
    title: {
      value: 'Assignment Type',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'reportingLevel': {
    title: {
      value: 'Reporting Level',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'leadConsultant': {
    title: {
      value: 'Lead Consultant',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'leadStillAtRRA': {
    title: {
      value: 'Lead Still at RRA',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'coLead': {
    title: {
      value: 'Co-Lead',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'region': {
    title: {
      value: 'Region (RRA)',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: false,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'officeName': {
    title: {
      value: 'RRA Office',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'hub': {
    title: {
      value: 'Hub (RRA)',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'confidentiality': {
    title: {
      value: 'Confidentiality',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'serviceLineType': {
    title: {
      value: 'Service Line Type',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
  'capability': {
    title: {
      value: 'Capability',
      isModified: false,
    },
    icon: 'text_fields',
    isFixedOrder: false,
    isVisible: {
      isModified: false,
      value: true,
    },
    isHeaderNameVisible: true,
    isDummy: false,
    isUserCreated: false,
    hasEditableCells: true,
    isModified: false,
    sortingState: {
      value: null,
      isModified: false,
    },
  },
};

export const ASSIGNMENT_DATE_COLUMNS: AssignmentTableColumnsKeysType[] = ['startDate', 'endDate'];

export const getPresentationSortDirection = (
  sortField: AssignmentTableColumnsKeysType,
): 'asc' | 'desc' => ASSIGNMENT_DATE_COLUMNS.includes(sortField) ? 'desc' : 'asc';

export const normalizePresentationSortingState = (
  sortingState: ITemplateSortingState | undefined,
): ITemplateSortingState | undefined => {
  const sortField = sortingState?.sorted_by_field;

  if (sortField && sortField !== 'manual'
    && sortingState.value === 'asc'
    && ASSIGNMENT_DATE_COLUMNS.includes(sortField)) {
    return {
      ...sortingState,
      value: getPresentationSortDirection(sortField),
    };
  }

  return sortingState;
};

// type guard
export const isAssignmentTableColumnKey = (key: any): key is AssignmentTableColumnsKeysType => {
  return AsisgnmentTableColumnsKeys.includes(key);
}

export const getFunding = (funding: {
  series?: IFundingHistory['series'],
}[] | undefined): string | undefined => {
  if (!funding || funding.length === 0) {
    return undefined;
  }

  const allSeriesNames = funding.map(fund => fund.series).filter(series => series !== undefined);
  if (allSeriesNames.length === 0) {
    return undefined;
  }

  return allSeriesNames.join(', ');
}

export const getAssignmentTableMappedColumnValue: IGetAssignmentTableMappedColumnValueParams = (
  {
    key,
    data,
    assignmentFieldsOverride,
    shouldOnlyCheckOverrides,
  }
) => {
  let formattedValue: string | number | undefined;
  let unformattedValue: string | number | undefined;
  // ! TODO: balraj - TS tells data wont be undefined but in UI we can still get this as undefined
  if (!data) {
    return {
      formattedValue: '',
      unformattedValue: '',
    };
  }
  const team = data.team;

  // overridden assigment fields values are considered first if available
  // ! TODO: balraj - TS doesnt catch the ?. optional chaining undefined
  const overrideAssignemntFieldValue = assignmentFieldsOverride ? assignmentFieldsOverride[data.ref]?.[key] as string | number | undefined : undefined;

  if (overrideAssignemntFieldValue !== undefined || shouldOnlyCheckOverrides) {
    return {
      formattedValue: overrideAssignemntFieldValue,
      unformattedValue: overrideAssignemntFieldValue,
    };
  }

  if (key === 'label') {
    formattedValue = unformattedValue = data?.label;
  } else if (key === 'clientName') {
    formattedValue = unformattedValue = data?.client_company.company_name;
  } else if (key === 'functionalArea') {
    formattedValue = unformattedValue = data?.functional_area;
  } else if (key === 'title') {
    formattedValue = unformattedValue = data?.title;
  } else if (key === 'assignmentStatus') {
    formattedValue = unformattedValue = data.status;
  } else if (key === 'startDate') {
    unformattedValue = data.start_date;
    formattedValue = formatDate(data?.start_date ?? null);
  } else if (key === 'clientCompanyActiveInvestors') {
    formattedValue = unformattedValue = data?.client_company?.investors?.map((datum) => datum?.name)?.filter((datum) => datum !== undefined)?.join(", ");
  } else if (key === 'endDate') {
    unformattedValue = data.end_date;
    formattedValue = formatDate(data.end_date ?? null);
  } else if (key === 'orgType') {
    formattedValue = unformattedValue = data.capiq_details?.org_type_raw ?? data.company_details?.org_type;
  } else if (key === 'personIndustry') {
    formattedValue = unformattedValue = getTeamLead(data.team)?.industry?.join(", ");
  } else if (key === 'industry') {
    formattedValue = unformattedValue = data?.client_company?.industry?.join(', ');
  } else if (key === 'location') {
    formattedValue = unformattedValue = data?.location_raw;
  } else if (key === 'positionClientLocationCompanyHq') {
    formattedValue = unformattedValue = data?.billing_client_company?.location;
  } else if (key === 'billingClientLocation') {
    formattedValue = unformattedValue = data?.client_company?.location;
  } else if (key === 'positionAssignmentRegion') {
    formattedValue = unformattedValue = data?.region?.join(', ');
  } else if (key === 'assignmentClientCompanyRegion') {
    formattedValue = unformattedValue = data?.client_company?.region?.join(', ');
  } else if (key === 'assignmentIndustry') {
    formattedValue = unformattedValue = data?.industry;
  } else if (key === 'assignmentBillingClientCompanyStockIndex') {
    formattedValue = unformattedValue = data?.billing_client_company?.stock_index?.join(", ");
  } else if (key === 'assignmentClientCompanyStockIndex') {
    formattedValue = unformattedValue = data?.client_company?.stock_index?.join(", ");
  } else if (key === 'assignmentIndustrySector') {
    formattedValue = unformattedValue = data?.industry_sector;
  } else if (key === 'assignmentIndustryPractice') {
    formattedValue = unformattedValue = data?.industry_practice;
  } else if (key === 'assignmentBillingClientCompanyParentCompanyName') {
    formattedValue = unformattedValue = data?.billing_client_company?.parent_company_name;
  } else if (key === 'assignmentClientCompanyParentCompanyName') {
    formattedValue = unformattedValue = data?.client_company?.parent_company_name;
  } else if (key === 'selectedCandidates') {
    const selectedCandidates = data?.selected_candidates;
    const selectedCandidatedNames = selectedCandidates
      ?.map((candidate) => candidate.name)
      ?.join(', ');

    formattedValue = unformattedValue = selectedCandidatedNames;
  } else if (key === 'hqLocation') {
    const hqLocation = data?.company_details?.hq_location;
    const location = decodeString(displayLocation(hqLocation));

    formattedValue = unformattedValue = location;
  } else if (key === 'billedClientName') {
    formattedValue = unformattedValue = data?.billing_client_company?.company_name;
  } else if (key === 'privateEquityType') {
    formattedValue = unformattedValue = data?.details?.PrivateEquityType;
  } else if (key === 'revenue') {
    // revenue_norm is a normalized range, not a value: a "less than X" bucket
    // normalizes to from: 0, so the display string resolves through
    // est_revenue. The numeric lower bound stays as the sort key.
    // See utils/companyRevenue (FPE-9613).
    const capiqFinancialInfo = data?.capiq_details?.curr_financial_info;
    const companyFinancialInfo = data?.company_details?.curr_financial_info;

    formattedValue =
      formatCompanyRevenue({
        financialInfo: capiqFinancialInfo,
        humanize: parseIntAndHumanizeCount
      }) ??
      formatCompanyRevenue({
        financialInfo: companyFinancialInfo,
        humanize: parseIntAndHumanizeCount
      });
    unformattedValue =
      capiqFinancialInfo?.revenue_norm?.from ?? companyFinancialInfo?.revenue_norm?.from;
  } else if (key === 'funding') {
    const fundingNames = getFunding(data?.company_details?.funding);

    formattedValue = unformattedValue = fundingNames;
  } else if (key === 'serviceLineGroup') {
    formattedValue = unformattedValue = data?.service_line_group;
  } else if (key === 'assignmentType') {
    formattedValue = unformattedValue = data?.assignment_type;
  } else if (key === 'leadConsultant') {
    const lead = getTeamLead(team)?.name;

    formattedValue = unformattedValue = lead;
  } else if (key === 'leadStillAtRRA') {
    const isLeadActive = getTeamLead(team)?.active;

    formattedValue = unformattedValue = isLeadActive;
  } else if (key === 'coLead') {
    const coLead = getTeamCoLead(team)
      ?.map((member) => member?.name)
      ?.join(', ');

    formattedValue = unformattedValue = coLead;
  } else if (key === 'region') {
    const region = getTeamLead(team)?.region;

    formattedValue = unformattedValue = region;
  } else if (key === 'officeName') {
    formattedValue = unformattedValue = data?.office_name;
  } else if (key === 'hub') {
    const officeName = getTeamLead(team)?.office_name;
    formattedValue = unformattedValue = officeName;
  } else if (key === 'serviceLineType') {
    formattedValue = unformattedValue = data?.service_line_type;
  } else if (key === 'capability') {
    formattedValue = unformattedValue = data?.capability?.join(', ');
  } else if (key === 'marketCap') {
    const marketCap = data?.capiq_details?.curr_financial_info?.market_cap;
    formattedValue = parseIntAndHumanizeCount(marketCap);
    unformattedValue = marketCap;
  } else if (key === 'companyEmployeesSize') {
    const employeesSize = data?.company_details?.comp_size?.[0]?.employees;
    formattedValue = parseIntAndHumanizeCount(employeesSize);
    unformattedValue = employeesSize;
  } else if (key === 'businessModel') {
    formattedValue = unformattedValue = data?.company_details?.biz_models?.join(', ');
  } else if (key === 'competitors') {
    const competitorsName = data?.company_details?.competitors?.map(competitor => competitor.name).filter(name => name !== undefined);

    if (!competitorsName) {
      formattedValue = unformattedValue = undefined;
    } else {
      formattedValue = unformattedValue = competitorsName.join(', ');
    }
  } else if (key === 'parentCompanyFindem') {
    const companyNames = data?.company_details?.parent_companies?.map(company => company.company_name).filter(name => name !== undefined);

    if (!companyNames) {
      formattedValue = unformattedValue = undefined;
    } else {
      formattedValue = unformattedValue = companyNames.join(', ');
    }
  } else if (key === 'reportingLevel') {
    const reportingLevel = data?.reporting_level;
    formattedValue = unformattedValue = reportingLevel;
  } else if (key === 'confidentiality') {
    const confidentiality = data?.confidentiality;
    formattedValue = unformattedValue = confidentiality;
  } else if (key === 'assignmentBillingClientCompanyRRACandidEstRevenue') {
    // The backend types this est_revenue as a string (IFinancialInfo in
    // firstcut), so it can be a bucket label. parseInt would turn "1M-5M"
    // into 1 (FPE-9613).
    const revenue = data?.billing_client_company?.rra_candid?.financial_data?.est_revenue;
    formattedValue = formatRevenueLabel({ estRevenue: revenue, humanize: parseIntAndHumanizeCount });
    unformattedValue = revenue ?? undefined;
  } else if (key === 'assignmentBillingClientCompanyRRACandidExpenses') {
    const expenses = data?.billing_client_company?.rra_candid?.financial_data?.expenses;
    formattedValue = parseIntAndHumanizeCount(expenses);
    unformattedValue = expenses ?? undefined;
  } else if (key === 'assignmentClientCompanyRRACandidEstRevenue') {
    const revenue = data?.client_company?.rra_candid?.financial_data?.est_revenue;
    formattedValue = formatRevenueLabel({ estRevenue: revenue, humanize: parseIntAndHumanizeCount });
    unformattedValue = revenue ?? undefined;
  } else if (key === 'assignmentClientCompanyRRACandidExpenses') {
    const expenses = data?.client_company?.rra_candid?.financial_data?.expenses;
    formattedValue = parseIntAndHumanizeCount(expenses);
    unformattedValue = expenses ?? undefined;
  }

  return {
    formattedValue,
    unformattedValue,
  }
}

// TODO: svg doesnt work
export const SUPPORTED_LOGO_UPLOAD_FILE_TYPES = ['.png', '.jpg', '.svg+xml'];

export interface ImgUploadDetails extends
  Omit<ICredBuilderImgUploadResponse, 'key'>,
  Omit<ICredBuilderImgUploadPayload, 'formData' | 'joid'> {
  key: string | undefined;
};
type UpdateMetadataParams = {
  currentMetadata: ICredential['metadata']; // can be undefined
  presentationState: PresentationState; // should always be passed
  imgUploadDetails?: ImgUploadDetails;
};

const createLegacyCssMetadataFromTileSlotLayout = ({
  tileSlotLayout,
  tilesAlignment,
}: Pick<PresentationState, 'tileSlotLayout' | 'tilesAlignment'>) => {
  if (!tileSlotLayout) {
    return {
      display: '',
      'grid-template-areas': '',
      'grid-template-columns': '',
    };
  }

  return {
    display: 'grid',
    'grid-template-areas': createGridTemplateAreasTemplateStandard({
      columns: tileSlotLayout.cols_per_row,
      maxColumns: MAX_COLS_TEMAPLTE_STANDARD,
      prefix: GRID_AREA_PREFIX,
      rows: tileSlotLayout.slots.length,
      tilesAlignment,
      value: tileSlotLayout.slots.flat().map((tileId) => tileId ?? ''),
    }),
    'grid-template-columns': generateGridTemplateColumnsTemplateStandard({
      colsPerRow: tileSlotLayout.cols_per_row,
      maxColsPerRow: MAX_COLS_TEMAPLTE_STANDARD,
      tilesAlignment,
    }),
  };
};

// common function to update metadata of a Credential
export const updateMetadata = ({
  currentMetadata,
  presentationState,
  imgUploadDetails,
}: UpdateMetadataParams): ICredentialMetaData => {
  const {
    templateKey,
    description,
    isCompanyLogoVisible,
    tilesAlignment,
    tileHeaderData,
    colsPerRow,
    rowsPerSlide,
    tileSlotLayout,
    assignmentFields,
    sortingState,
    presentationSlideTitle,
    privateAndConfidential,
    companyOfficialLogo,
    presentationSlideNumber,
  } = presentationState;
  const currentDateISO = new Date().toISOString();
  const currentTimestamp = Date.now();
  const metadataColsPerRow = tileSlotLayout
    ? tileSlotLayout.cols_per_row
    : colsPerRow;
  const metadataRowsPerSlide = tileSlotLayout
    ? tileSlotLayout.rows_per_slide
    : rowsPerSlide;

  const updatedMetadata = currentMetadata ? structuredClone(currentMetadata) : { active_template: templateKey };
  let activeTemplateMetadata = updatedMetadata[templateKey];

  // set the current active template key - base metadata
  updatedMetadata.active_template = templateKey;

  // modify the selected template - template metadata
  activeTemplateMetadata = {
    ...activeTemplateMetadata,
    presentation_slide_title: {
      ...presentationSlideTitle,
      updated_at_iso: currentDateISO,
      updated_at_unix_timestamp: currentTimestamp,
    },
    private_and_confidential: {
      ...privateAndConfidential,
      updated_at_iso: currentDateISO,
      updated_at_unix_timestamp: currentTimestamp,
    },
    slide_description: description,
    company_official_logo: {
      ...companyOfficialLogo,
      updated_at_iso: currentDateISO,
      updated_at_unix_timestamp: currentTimestamp,
    },
    presentation_slide_number: {
      ...presentationSlideNumber,
      updated_at_iso: currentDateISO,
      updated_at_unix_timestamp: currentTimestamp,
    },
    sorting_state: sortingState ? {
      ...sortingState,
      updated_at_iso: currentDateISO,
      updated_at_unix_timestamp: currentTimestamp
    } : DEFAULT_SORTING_STATE,
    assignment_fields: assignmentFields ?
      assignmentFields.map((field) => field.columnKey) :
      DEFAULT_APPLIED_FIELDS_STANDARD_TEMPLATES.map((field) => field.columnKey),
    is_company_logo_visible: isCompanyLogoVisible,
    tile_header_data: tileHeaderData,
    tiles_alignment: tilesAlignment,
    cols_per_row: metadataColsPerRow,
    rows_per_slide: metadataRowsPerSlide,
    tile_slot_layout: tileSlotLayout ?? undefined,
    css: createLegacyCssMetadataFromTileSlotLayout({
      tileSlotLayout,
      tilesAlignment,
    }),
    // updates current timestamp
    updated_at_iso: currentDateISO,
    updated_at_unix_timestamp: currentTimestamp,
  };

  const createdAtISO = activeTemplateMetadata?.created_at_iso;
  const createdAtUnixTimestamp = activeTemplateMetadata?.created_at_unix_timestamp;

  // creating tempplate for the 1st time
  const templateInitialise = createdAtISO === undefined || createdAtUnixTimestamp === undefined;

  if (templateInitialise) {
    activeTemplateMetadata['created_at_iso'] = currentDateISO;
    activeTemplateMetadata['created_at_unix_timestamp'] = currentTimestamp;
  }

  // img upload details
  if (imgUploadDetails) {
    const { key, source, uniqueKey } = imgUploadDetails;

    if (source === 'companyLogo') { // Company logo
      activeTemplateMetadata['company_logo'] = key;
    } else if (source === 'assignmentLogoOverride' && uniqueKey) { // Assignment logo override
      updatedMetadata['assignments_logo_override'] = {
        ...updatedMetadata['assignments_logo_override'],
        [uniqueKey]: key
      };
    } else { // Should be unreachable code path
      const msg = `Unknown img upload source: ${source}\n Add handling for this source`
      console.error(msg);
      throw new Error(msg);
    }
  }

  updatedMetadata[templateKey] = activeTemplateMetadata;

  return updatedMetadata;
}

// common accessors functions - start
export const getTeamLead = (team: ITeam[] | undefined) => {
  return team?.find((member) => member?.role?.toLowerCase() === 'lead');
}

export const getTeamCoLead = (team: ITeam[] | undefined) => {
  return team?.filter((member) => member?.role?.toLowerCase() === 'clead');
}

// common accessors functions - end

export const displayLocation: (
  location: Partial<ILocation> | undefined,
  locationRaw?: string | undefined,
  address?: string | undefined
) => string | undefined = (location, locationRaw, address) => {
  if (address && address.length > 0) {
    return address;
  }
  if (locationRaw && locationRaw.length > 0) {
    return locationRaw;
  }
  if (!location) {
    return undefined;
  }
  const returnLocation = [];

  if (location.city) {
    returnLocation.push(location.city);
  }

  if (location.state_short || location.state) {
    returnLocation.push(location.state_short ?? location.state);
  }

  if (location.country_short || location.country) {
    returnLocation.push(
      returnLocation.length
        ? location.country_short ?? location.country
        : location.country ?? location.country_short
    );
  }

  return returnLocation.splice(0, 2).join(', ');
};


export const parseIntAndHumanizeCount = (data: number | string | null | undefined) => {
  if (data === undefined || data === null || (typeof data === 'string' && data.trim().length === 0)) {
    return undefined;
  }

  const numericValue = typeof data !== 'number' ? parseInt(data) : data;

  if (isNaN(numericValue)) {
    return data;
  }

  return humanizeCount(numericValue);
};


// used in Assignment Table
export const isRowShortlisted = (
  ref: string,
  shortlistedAssignments: string[],
  hoverAction: HoverAction
): boolean => {
  return shortlistedAssignments.includes(ref) && hoverAction !== 'remove';
}

// Styles for pinning the column in Assignments Table
export const getCommonPinningStyles = (column: Column<IAssigmentsTableData>): CSSObject => {
  const isPinned = column.getIsPinned();
  const _isLastLeftPinnedColumn = isPinned === "left" && column.getIsLastColumn("left");
  const _isFirstRightPinnedColumn = isPinned === "right" && column.getIsFirstColumn("right");

  return {
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    opacity: isPinned ? 1 : 1,
    position: isPinned ? "sticky" : "relative",
    width: `${column.getSize()}px`,
    backgroundColor: isPinned ? "white" : "",
    zIndex: isPinned ? 1 : 0,
    borderRight: column.id !== "select" ? "1px solid lightgray" : "",
  };
};

// the hired candidate for the assignment
export const getSelectedCandidatesDetails = ({
  selectedCandidates
}: {
  selectedCandidates: IAssigmentsTableData['selected_candidates']
}): Partial<ISelectedCandidates> | undefined => {

  return selectedCandidates[0] ?? undefined;
};

/**
 * Sort refs consistently as layout depends on the ordering of refs
*/
export const sortGroupedAssignmentKeys: SortGroupedAssignmentKeys = ({
  groupedAssignment
}) => {

  return (Object.keys(groupedAssignment) as GroupedAssignmentsKey[])
    .filter((datum) => datum !== 'clientCompanyId' && datum !== 'id')
    // handles numbers inside strings ["item2", "item10", "item1"] -> ["item1", "item2", "item10"]
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

export const getAssignmentRefsFromGroupedAssignments = ({
  groupedAssignments,
  refsSorter,
}: {
  groupedAssignments: NullableGroupedAssignments;
  refsSorter: SortGroupedAssignmentKeys;
}) => {
  const assignmentsRefs = [];

  if (!groupedAssignments) {
    return [];
  }

  for (const groupedAssignment of groupedAssignments) {
    // sort refs to maintain consistency in the ordering of the refs to be consumed by the layout
    const refs = refsSorter({ groupedAssignment });
    const formattedRefs = refs.join(GROUPED_ASSIGNMENTS_REFS_DELIMITER);

    assignmentsRefs.push(formattedRefs);
  }

  return assignmentsRefs;
};

/**
 * Group assignments into one card per client company, then order the cards with
 * the active sorting state. Real assignments are ref-sorted first and manual
 * entries are appended after them, so a new manual card starts last and only
 * moves if the field sort moves it.
 *
 * Example, sorting on clientName asc:
 *
 * assignments:      [{ ref: "r2", company: "c1"/"Acme" },
 *                    { ref: "r1", company: "c1"/"Acme" },
 *                    { ref: "r3", company: "c2"/"Meridian" }]
 * assignmentsManual: { "ab_manual_created_assignment": { company: "c3"/undefined } }
 *
 * ->  [ { clientCompanyId: "c1", r1: {...}, r2: {...} },   // ref-sorted inside the card
 *       { clientCompanyId: "c2", r3: {...} },
 *       { clientCompanyId: "c3", ab_manual_created_assignment: {...} } ]  // no name, sorts last
 */
export const groupAssignmentsByClientId = ({
  assignments,
  assignmentFieldsOverride,
  sortingState,
  gridTemplateAreas,
  assignmentsManual,
}: {
  assignments: IAssigmentsTableData[];
  sortingState: ITemplateSortingState | undefined;
  assignmentFieldsOverride: IBaseCredentialMetaData['assignment_fields_override'];
  gridTemplateAreas: string | undefined;
  assignmentsManual: IBaseCredentialMetaData['assignments_manual'];
}): {
  groupedByClientCompanyIdAssignments: GroupedAssignments[];
} => {
  const assignmentsManualValues = Object.values(assignmentsManual ?? {});
  const sortedAssignments = [...assignments]
    // handles numbers inside strings ["item2", "item10", "item1"] -> ["item1", "item2", "item10"]
    .sort((a, b) => a.ref.localeCompare(b.ref, undefined, { numeric: true }));
  const groupedAssignments = [...sortedAssignments, ...assignmentsManualValues]
    .reduce((acc, value) => {
      const clientCompanyId = getClientCompanyId({
        data: value,
      });

      const groupedAssignmentIndex = acc.findIndex((value => value.clientCompanyId === clientCompanyId));

      if (groupedAssignmentIndex === -1) {
        acc.push({
          clientCompanyId,
          id: clientCompanyId,
          [value.ref]: value
        } as GroupedAssignments);

        return acc;
      }

      acc[groupedAssignmentIndex][value.ref] = value;
      return acc;
    }, [] as GroupedAssignments[]);

  const { sortedGroupedAssignments: groupedByClientCompanyIdAssignments } = sortGroupedAssignments({
    sortingState,
    assignmentFieldsOverride,
    groupedAssignments,
    gridTemplateAreas,
    sortAgainstCurrentTemplate: true,
  })

  return {
    groupedByClientCompanyIdAssignments,
  }
}

/**
 * Build the comparable string for one grouped card under the active sort field.
 * Every assignment line in the card contributes, so a grouped card sorts by its
 * whole content. Returns an empty string when the card has no value for the
 * field at all, which callers treat as "sort last".
 *
 * "No value" means every line resolved to undefined or an empty string. A present
 * falsy value still counts as a value: a boolean field such as leadStillAtRRA
 * contributes "false", which sorts among real values rather than sinking.
 *
 * Examples, given a card holding two assignment lines:
 *
 * sortField "clientName", both lines on Acme      -> "Acme Acme"
 * sortField "title", lines "Chief Executive"      -> "Chief Executive Board Director"
 *   then "Board Director"                            (card key order, not re-sorted here)
 * sortField "clientName", one named line and one  -> "Acme"      (partly filled still
 *   line with no company name                                     counts as a value)
 * sortField "leadStillAtRRA", lead active false   -> "false"     (a value, sorts normally)
 * sortField "clientName", manual card with only a -> ""          (no value, sorts last)
 *   company_id and a title override
 */
const getGroupedAssignmentSortValue = ({
  assignmentFieldsOverride,
  groupedAssignment,
  sortField,
}: {
  assignmentFieldsOverride: IBaseCredentialMetaData['assignment_fields_override'];
  groupedAssignment: GroupedAssignments;
  sortField: AssignmentTableColumnsKeysType;
}): string => {
  let groupSortValue = "";

  for (const assignment in groupedAssignment) {
    if (isNonAssignmentKeyInGroupedAssignment({ id: assignment })) {
      continue;
    }

    const data = groupedAssignment[assignment];
    let { formattedValue } = getAssignmentTableMappedColumnValue({ key: sortField, data, assignmentFieldsOverride });
    if (formattedValue === undefined) {
      continue;
    } else if (typeof formattedValue === 'number') {
      formattedValue = String(formattedValue);
    }

    groupSortValue += formattedValue + " ";
  }

  return groupSortValue.trim();
};

const getGroupedAssignmentLatestDateSortValue = ({
  assignmentFieldsOverride,
  groupedAssignment,
  sortField,
}: {
  assignmentFieldsOverride: IBaseCredentialMetaData['assignment_fields_override'];
  groupedAssignment: GroupedAssignments;
  sortField: AssignmentTableColumnsKeysType;
}): number | undefined => {
  let latestDateValue: number | undefined;

  for (const assignment in groupedAssignment) {
    if (isNonAssignmentKeyInGroupedAssignment({ id: assignment })) {
      continue;
    }

    const data = groupedAssignment[assignment];
    const { unformattedValue } = getAssignmentTableMappedColumnValue({
      key: sortField,
      data,
      assignmentFieldsOverride,
    });

    if (unformattedValue === undefined || unformattedValue === '') {
      continue;
    }

    const parsedDate = dayjs(unformattedValue);
    if (!parsedDate.isValid()) {
      continue;
    }

    latestDateValue = Math.max(latestDateValue ?? Number.NEGATIVE_INFINITY, parsedDate.valueOf());
  }

  return latestDateValue;
};

/**
 * Sorts depending on the sorting state
 *
 * Examples, given three cards - Meridian, one with no client name, and Acme:
 *
 * { sorted_by_field: "clientName", value: "asc"  } -> [Acme, Meridian, no-name]
 * { sorted_by_field: "clientName", value: "desc" } -> [Meridian, Acme, no-name]
 * { sorted_by_field: "manual", value: "manual"   } -> incoming order, unchanged
 *   with no gridTemplateAreas                         [Meridian, no-name, Acme]
 * { sorted_by_field: null, value: null           } -> [Acme, Meridian, no-name]
 *
 * The no-name card lands last in both directions by rule, not by string compare.
 * A manual/manual state with a gridTemplateAreas value instead replays the saved
 * grid order and appends any card the grid does not mention.
 *
 * Note the last example: an explicitly null sorting state does not mean "leave the
 * order alone". Both fields are read with ??, which treats null as absent, so a
 * null state falls back to DEFAULT_SORTING_STATE and sorts by clientName asc.
 */
export const sortGroupedAssignments: SortGroupedAssignments = ({
  groupedAssignments,
  assignmentFieldsOverride,
  sortingState,
  gridTemplateAreas,
  sortAgainstCurrentTemplate = false, // can be used for validation checks
}) => {
  const sortValue = sortingState?.value ?? DEFAULT_SORTING_STATE.value;
  const sortField = sortingState?.sorted_by_field ?? DEFAULT_SORTING_STATE.sorted_by_field;
  const shouldNotSortFromSortState = !sortValue || !sortField || sortValue === 'manual' || sortField === 'manual';
  let sortedGroupedAssignments: GroupedAssignments[] = [];

  // preserve manual ordering
  if (sortValue === 'manual' && sortField === 'manual') {
    if (!gridTemplateAreas) {
      // not enought info to sort
      return {
        sortedGroupedAssignments: groupedAssignments,
      }
    };

    if (sortAgainstCurrentTemplate) {
      const { groupedAssignmentIds, groupedAssignmentsById } = getGroupedOrderedAssignmentsFromLayout({
        prefix: GRID_AREA_PREFIX,
        groupedAssignmentsRefsDelimiter: GROUPED_ASSIGNMENTS_REFS_DELIMITER,
        gridTemplateAreas,
        groupedAssignments,
      });
      const orderedGroupedAssignments = new Set<GroupedAssignments>();

      for (const groupedAssignmentId of groupedAssignmentIds) {
        const groupedAssignment = groupedAssignmentsById[groupedAssignmentId];
        if (groupedAssignment && !orderedGroupedAssignments.has(groupedAssignment)) {
          sortedGroupedAssignments.push(groupedAssignment);
          orderedGroupedAssignments.add(groupedAssignment);
        }
      }

      // A saved manual grid can be stale when assignments are added after drag/drop.
      // Preserve saved order first, then keep any current groups missing from the grid
      // so template validation can regenerate instead of silently hiding them.
      for (const groupedAssignment of groupedAssignments) {
        if (!orderedGroupedAssignments.has(groupedAssignment)) {
          sortedGroupedAssignments.push(groupedAssignment);
        }
      }
    }


    return {
      sortedGroupedAssignments: sortAgainstCurrentTemplate ? sortedGroupedAssignments : groupedAssignments, // if sort preffered then return sorted data else return as is
    }
  }

  sortedGroupedAssignments = shouldNotSortFromSortState
    ? [...groupedAssignments] :
    groupedAssignments.toSorted((assignmentsA, assignmentsB) => {
      if (!sortValue || !sortField) {
        return 0;
      }

      if (ASSIGNMENT_DATE_COLUMNS.includes(sortField)) {
        const dateValueA = getGroupedAssignmentLatestDateSortValue({
          assignmentFieldsOverride,
          groupedAssignment: assignmentsA,
          sortField,
        });
        const dateValueB = getGroupedAssignmentLatestDateSortValue({
          assignmentFieldsOverride,
          groupedAssignment: assignmentsB,
          sortField,
        });

        if (dateValueA === undefined || dateValueB === undefined) {
          if (dateValueA === undefined && dateValueB === undefined) {
            return 0;
          }

          return dateValueA === undefined ? 1 : -1;
        }

        const comparison = dateValueA - dateValueB;
        return sortValue === 'desc' ? -comparison : comparison;
      }

      // for each of the assignment in a grouped assigment
      // get the formatted value, join them and do string sort
      const formattedValueA = getGroupedAssignmentSortValue({
        assignmentFieldsOverride,
        groupedAssignment: assignmentsA,
        sortField,
      });
      const formattedValueB = getGroupedAssignmentSortValue({
        assignmentFieldsOverride,
        groupedAssignment: assignmentsB,
        sortField,
      });

      // Cards with no value for the active sort field always sink to the bottom, in
      // both directions. A plain string compare puts empty values first ascending,
      // which floats a manually added card - it has no client company name until the
      // user overrides one - above every real assignment.
      // Empty cards tie, and toSorted is stable, so they keep the order they arrived
      // in: insertion order on a fresh build, and the previous sorted order when an
      // already-sorted list is re-sorted.
      if (!formattedValueA || !formattedValueB) {
        if (!formattedValueA && !formattedValueB) {
          return 0;
        }

        return formattedValueA ? -1 : 1;
      }

      const comparison = formattedValueA.localeCompare(formattedValueB);
      return sortValue === 'desc' ? -comparison : comparison;
    });

  return {
    sortedGroupedAssignments: sortedGroupedAssignments,
  };
}

export const generateDefaultColsData: IGenerateDefaultColsData = ({ defaultCols, assignmentTableEdits }) => {
  const tableColumns: ITableColumns = {
    columns: {
      // @ts-expect-error Column ids are assigned dynamically before this object is returned.
      by_id: {},
      all_ids: []
    }
  };

  // add data for default columns
  for (const column of Object.keys(defaultCols) as AssignmentTableColumnsKeysType[]) {
    tableColumns['columns']['by_id'][column] = defaultCols[column];
    if (!defaultCols[column].isDummy) {
      tableColumns['columns']['all_ids'].push(column); // push dummy cols at the end
    }
  }

  // add saved cols and the order of the cols
  if (assignmentTableEdits?.columns) {
    if (assignmentTableEdits.columns.all_ids.length) {
      tableColumns['columns']['all_ids'] = [];
    }

    /**
     * Persisted table options can outlive the hard-coded column registry.
     * Removed columns may still exist in old credentials' all_ids and would
     * later crash consumers that assume AssignmentTableColumns[colId] exists.
     * Skip unknown ids, and use the default definition for known ids when
     * saved by_id data is missing so malformed metadata still hydrates safely.
     */
    for (const colId of assignmentTableEdits.columns.all_ids) {
      if (!isAssignmentTableColumnKey(colId)) {
        continue;
      }

      const savedColsData = structuredClone(assignmentTableEdits?.columns?.by_id?.[colId] ?? defaultCols[colId]);
      /** Handling for legacy data - start
      * Add handling for legacy data where fields like modifiedBy, timestamp, isModified were not tracked at the field level
      */
      for (const colField of EDITABLE_COLUMN_FIELDS) {
        if (savedColsData[colField]?.value === undefined) {
          savedColsData[colField] = {
            // @ts-expect-error - TS error due to legacy data structure for the same values, safe to suppress
            isModified: false,
            value: savedColsData[colField],
          }
        }
      }
      // Handling for legacy data - end

      tableColumns['columns']['by_id'][colId] = savedColsData;
      if (!savedColsData.isDummy) {
        tableColumns['columns']['all_ids'].push(colId); // push dummy cols at the end
      }
    }
  }

  // handles case if default cols are not there in saved data, this can happen if we introduce new default cols
  for (const column of Object.keys(defaultCols) as AssignmentTableColumnsKeysType[]) {
    if (!tableColumns['columns']['all_ids'].includes(column)) {
      tableColumns['columns']['all_ids'].push(column);
    }
  }

  return tableColumns;
};

export const COMPANY_DETAILS_PROJECTION_ASSIGNMENT_TABLE: Record<string, number> = {
  // capiq fields
  "capiq.curr_financial_info.revenue_norm.from": 1,
  // revenue_norm.from alone renders as 0 for bucketed revenue; the label is
  // the only displayable value in that case (FPE-9613).
  "capiq.curr_financial_info.revenue_norm.to": 1,
  "capiq.curr_financial_info.est_revenue": 1,
  "capiq.org_type_raw": 1,
  "capiq.curr_financial_info.market_cap": 1,
  // company db
  "company.curr_financial_info.revenue_norm.from": 1,
  "company.curr_financial_info.revenue_norm.to": 1,
  "company.curr_financial_info.est_revenue": 1,
  "company.hq_location": 1,
  "company.org_type": 1,
  "company.comp_size": 1,
  "company.funding.series": 1,
  "company.biz_models": 1,
  "company.competitors.name": 1,
  "company.parent_companies.company_name": 1,
}

export const dateSorter: SortingFn<IAssigmentsTableData> = (rowA, rowB, columnId) => {
  const parseDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return null; // Handle empty, null, undefined
    const parsedDate = new Date(dateStr);
    return isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const dateA = parseDate(rowA.getValue(columnId));
  const dateB = parseDate(rowB.getValue(columnId));

  // Handle cases where one or both dates are null
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1; // Move empty values to the bottom
  if (!dateB) return -1;

  return dateA.getTime() - dateB.getTime();
};

export const getClientCompanyId = ({
  data,
}: {
  data: IAssigmentsTableDataWithRequiredIds;
}): string => {
  return data.client_company.company_id;
};


/**
 * Preferred logo from the assignment data. Doesnt consider user uploaded logos.
*/
export const getPrimaryLogoUrl: GetPrimaryLogoUrl = ({
  logos,
  source = 'linkedin', // Default is findem sourced logo (linkedin)
}) => {
  if (!logos?.length) return { logo: undefined, url: undefined };

  // Find logo by source with linkedin fallback
  let logo: CredLogo | undefined;
  let linkedinLogo: CredLogo | undefined;

  for (const logoItem of logos) {
    if (logoItem.source === source) {
      logo = logoItem;
      break; // Found exact match, no need to continue
    }
    if (logoItem.source === 'linkedin') {
      linkedinLogo = logoItem; // Keep linkedin as fallback
    }
  }

  // Use linkedin fallback if no exact match found
  if (!logo) {
    logo = linkedinLogo;
  }

  if (!logo) {
    return {
      url: undefined,
      logo: undefined,
    }
  }

  const url = logo.source === 'linkedin' ? `${S3ImageBaseUrl}/${logo.source_ref}.jpg` : logo.url;

  return { logo, url };
}

// Helper function to generate auto credential name
export const generateAutoCredentialName = (): string => {

  return `New Credential`;
};
